'use client'

import { useHydration } from '@/hooks/use-hydration'
import { motion, AnimatePresence } from 'framer-motion'
import ExportedImage from 'next-image-export-optimizer'
import { useState } from 'react'

import { Loading } from '@/components/atoms/icons'
import { Switch } from '@/components/atoms/ui/switch'
import { CorporateCard } from '@/components/molecules/cards/corporate-card'
import Container from '@/components/templates/container/container'

interface Props {
  data: Corporate
}

export const CorporateSolution = ({ data }: Props) => {
  const isHydrated = useHydration()
  const [category, setCategory] = useState<'servicePmdn' | 'servicePma'>('servicePmdn')

  const handleSwitchChange = (val: boolean) => {
    setCategory(val ? 'servicePma' : 'servicePmdn')
  }

  const corporate = data.items[0]
  if (!isHydrated)
    return (
      <div className="flex items-center justify-center bg-primary-blue w-full h-[90vh]">
        <Loading className="text-white w-9 h-9" />
      </div>
    )

  return (
    <div className="relative lg:bg-[url(/static/images/hero-corporate-solution.png)] h-full bg-no-repeat bg-top lg:bg-contain w-full">
      <ExportedImage
        src="/static/images/hero-corporate-solution.png"
        alt=""
        width={600}
        height={1000}
        className="absolute top-0 object-cover object-bottom w-full lg:hidden h-[80vh]"
      />
      <Container className="relative z-10 space-y-6 lg:space-y-10 py-14 lg:py-20">
        <header className="text-center max-w-[700px] mx-auto">
          <h1 className="pb-3.5 lg:pb-6 text-2xl font-extrabold text-white lg:text-5xl">
            {corporate.fields.title ?? 'Corporate Solution'}
          </h1>
          <p className="text-sm font-medium text-white lg:text-lg">
            {corporate.fields.subTitle ??
              'Dengan layanan ini, Anda bisa mendapatkan pengurusan dokumen legalitas dan compliance dalam 1 paket saja yang telah disesuaikan dengan kebutuhan perusahaan Anda.'}
          </p>
        </header>

        <section className="space-y-10 max-w-[1100px] mx-auto">
          <h2 className="text-xl font-bold text-center lg:text-2xl text-secondary-yellow">
            {corporate.fields.label || 'Paket Berlangganan Corporate Solution'}
          </h2>

          <nav className="flex items-center justify-center gap-6 font-semibold text-white lg:text-lg">
            <span>Investasi PMDN</span>
            <Switch onCheckedChange={handleSwitchChange} className="data-[state=unchecked]:bg-secondary-yellow" />
            <span>Investasi PMA</span>
          </nav>

          <article className="grid gap-8 lg:grid-cols-2">
            <AnimatePresence mode="wait">
              {corporate.fields[category].map((service, index) => {
                const entry = data.includes.Entry.find((item) => item.sys.id === service.sys.id)
                if (!entry) return null

                return (
                  <motion.div
                    key={entry.sys.id}
                    initial={{ opacity: 0, y: 20 }}
                    animate={{ opacity: 1, y: 0 }}
                    exit={{ opacity: 0, y: -20 }}
                    transition={{ duration: 0.3 }}
                  >
                    <CorporateCard {...entry.fields} isEven={index % 2 === 0} />
                  </motion.div>
                )
              })}
            </AnimatePresence>
          </article>
        </section>
      </Container>
    </div>
  )
}
